import { Calendar, Clock, MapPin } from 'lucide-react';
import { Match } from 'modules/client/tournaments/domain/types';
import React from 'react';
import UDCard from 'ud-ui/components/card';

interface FixturesSectionProps {
  matches: Match[];
}

const FixturesSection: React.FC<FixturesSectionProps> = ({ matches }) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <span className="animate-pulse rounded-full bg-red-600 px-2 py-1 text-xs font-medium text-white">LIVE</span>;
      case 'finished':
        return <span className="rounded-full bg-gray-600 px-2 py-1 text-xs font-medium text-white">Закончен</span>;
      case 'scheduled':
        return <span className="rounded-full bg-blue-600 px-2 py-1 text-xs font-medium text-white">Запланирован</span>;
      default:
        return null;
    }
  };

  const upcomingMatches = matches.filter((m) => m.status === 'scheduled');
  const recentMatches = matches.filter((m) => m.status === 'finished');

  return (
    <div className="space-y-6">
      {/* Upcoming Fixtures */}
      <UDCard variant="elevated" padding="none" className="overflow-hidden">
        <div className="bg-blue-600 px-6 py-4 text-white">
          <h2 className="flex items-center space-x-2 text-xl font-bold">
            <Calendar size={20} />
            <span>Предстоящие матчи</span>
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {upcomingMatches.map((match) => (
            <div key={match.id} className="p-6 transition-colors hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">{match.date}</span>
                    {getStatusBadge(match.status)}
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-1 items-center space-x-2">
                      <img src={match.homeTeam.logo} alt="logo" className="size-20 rounded-lg object-contain" />
                      <span className="font-medium text-gray-900">{match.homeTeam.name}</span>
                    </div>
                    <div className="font-bold text-gray-400">VS</div>
                    <div className="flex flex-1 items-center justify-end space-x-2">
                      <span className="font-medium text-gray-900">{match.awayTeam.name}</span>
                      <img src={match.awayTeam.logo} alt="logo" className="size-20 rounded-lg object-contain" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Clock size={14} />
                  <span>{match.time}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MapPin size={14} />
                  <span>{match.venue}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </UDCard>

      {/* Recent Results */}
      <UDCard variant="elevated" padding="none" className="overflow-hidden">
        <div className="bg-emerald-600 px-6 py-4 text-white">
          <h2 className="text-xl font-bold">Последние результаты</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {recentMatches.map((match) => (
            <div key={match.id} className="p-6 transition-colors hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-emerald-600">{match.date}</span>
                    {getStatusBadge(match.status)}
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-1 items-center space-x-2">
                      <span className="text-lg">
                        <img src={match.homeTeam.logo} alt="logo" className="size-20 rounded-lg object-contain" />
                      </span>
                      <span className="font-medium text-gray-900">{match.homeTeam.name}</span>
                    </div>
                    <div className="rounded-lg bg-gray-100 px-4 py-2">
                      <span className="text-lg font-bold text-gray-900">
                        {match.homeScore} - {match.awayScore}
                      </span>
                    </div>
                    <div className="flex flex-1 items-center justify-end space-x-2">
                      <span className="font-medium text-gray-900">{match.awayTeam.name}</span>
                      <span className="text-lg">
                        <img src={match.awayTeam.logo} alt="logo" className="size-20 rounded-lg object-contain" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-1">
                  <Clock size={14} />
                  <span>{match.time}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MapPin size={14} />
                  <span>{match.venue}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </UDCard>
    </div>
  );
};

export default FixturesSection;
